/**
 * `describeEvent` — the ONE source of truth turning a raw `task_events` row
 * into a **factual provenance line** for the node-history view (B3.1) and the
 * state timeline (B3.2). Pure, dependency-free over the wire shape + a couple
 * of small lookups; both surfaces read it so the phrasing can never diverge.
 *
 * Issue #763 (Phase 3, temporal frontend).
 *
 * Design refs:
 *   - §A4 (temporal model): per-node history is `task_events` filtered by
 *     `node_id` — "this node moved to Done on May 3 (Alex)." This module is
 *     where one event becomes that sentence.
 *   - §A9 (BRAND FIREWALL, sharpened for temporal): history is **provenance +
 *     collaboration context, NEVER a scorecard**. Every phrase here describes
 *     what happened to *the node* ("Moved to Done", "Assigned Alex"). It is
 *     deliberately FACTUAL, never evaluative — no "fast", "slow", "late", no
 *     count or rate about a person. The actor is shown as context ("by Alex"),
 *     never aggregated. The banlist applies to every string below.
 *   - §A6: the four factual states; state phrasing reuses `TASK_STATES`
 *     labels/colours (via `taskStateStyle`) so "Done" looks identical
 *     everywhere.
 *
 * The actor (`actor_membership_id`) is resolved to a display name + initials by
 * the caller (via `useTaskTeamMembers`); a null / unknown membership (e.g. a
 * member hard-deleted, FK `SET NULL`) degrades to a neutral "a teammate" and a
 * "—" avatar — never a crash, never a leak.
 */

import { type TaskState, taskStateStyle } from "../tree/state";

/**
 * The wire shape of one history event, mirrored from
 * `schema.gen.ts` `NodeHistoryItem`. We narrow `payload` to a readable record
 * (the generated type is the opaque `Record<string, never>`) — every field
 * read below is optional + defended, so a missing/odd payload degrades to a
 * plain phrase rather than throwing.
 */
export type TaskEventType =
  | "node_created"
  | "node_renamed"
  | "node_redescribed"
  | "node_reparented"
  | "node_reordered"
  | "node_assigned"
  | "node_unassigned"
  | "state_changed"
  | "dependency_added"
  | "dependency_removed"
  | "note_added"
  | "note_removed"
  | "node_archived"
  | "target_date_set";

export interface NodeHistoryEvent {
  readonly id: string;
  readonly event_type: TaskEventType | string;
  readonly actor_membership_id: string | null;
  readonly occurred_at: string;
  readonly payload: Record<string, unknown> | null | undefined;
}

/** How the caller resolves a membership id + a referenced node id to display text. */
export interface DescribeLookups {
  /**
   * membership id → display name (already team-curated); null/unknown → null.
   * Used to name the person an assign/unassign event refers to (the payload's
   * `membership_id`) — context, never a tally about them (§A9).
   */
  readonly memberName?: (membershipId: string | null) => string | null;
  /** node id → title, for "created under '…'" / "comes after '…'"; unknown → null. */
  readonly nodeTitle?: (nodeId: string | null | undefined) => string | null;
}

/** A neutral, never-evaluative label for an unresolved actor (§A9). */
export const UNKNOWN_ACTOR = "a teammate";

/** A described event, ready to render. */
export interface DescribedEvent {
  readonly id: string;
  /** The factual phrase, e.g. "Moved to Done" or "Assigned Dr. Wong". */
  readonly phrase: string;
  /**
   * For a `state_changed` event: the destination state, so the view can tint
   * the phrase with the `TASK_STATES` colour (the SVG renders "→ In progress"
   * in the state's indigo). `null` for every other event type.
   */
  readonly toState: TaskState | null;
  /** Colour to tint the phrase (state fg for state changes, else neutral ink). */
  readonly accent: string | null;
}

const NEUTRAL_INK = "#0C2A2A";

function asRecord(payload: NodeHistoryEvent["payload"]): Record<string, unknown> {
  return payload && typeof payload === "object" ? payload : {};
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function quote(label: string | null, fallback: string): string {
  return label ? `“${label}”` : fallback;
}

function isTaskState(v: unknown): v is TaskState {
  return v === "planned" || v === "in_progress" || v === "blocked" || v === "done";
}

/**
 * Format an ISO `YYYY-MM-DD` target date into a short "Jun 15, 2026" label for
 * a history phrase. Parsed LOCALLY (never `new Date(iso)`, which would shift by
 * the UTC offset and could read as the wrong day); an unparseable value degrades
 * to the raw string rather than throwing.
 */
function formatHistoryDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Turn one event into a factual provenance phrase + optional state accent.
 * Exhaustive over the known event types; an unknown future type degrades to a
 * calm generic "Updated" rather than throwing (the log is additive-only).
 */
export function describeEvent(
  event: NodeHistoryEvent,
  lookups: DescribeLookups = {},
): DescribedEvent {
  const p = asRecord(event.payload);
  const nodeTitle = (id: string | null | undefined) => lookups.nodeTitle?.(id) ?? null;
  const memberName = (id: string | null | undefined) => lookups.memberName?.(id ?? null) ?? null;

  let phrase = "Updated";
  let toState: TaskState | null = null;

  switch (event.event_type) {
    case "node_created": {
      const parentTitle = nodeTitle(str(p.parent_id));
      phrase = parentTitle ? `Created under ${quote(parentTitle, "")}` : "Created";
      break;
    }
    case "node_renamed": {
      const to = str(p.to);
      phrase = to ? `Renamed to ${quote(to, "")}` : "Renamed";
      break;
    }
    case "node_redescribed":
      phrase = "Description updated";
      break;
    case "node_reparented": {
      const to = nodeTitle(str(p.to));
      phrase = to ? `Moved under ${quote(to, "")}` : "Moved to a new parent";
      break;
    }
    case "node_reordered":
      phrase = "Reordered";
      break;
    case "node_assigned": {
      const name = memberName(str(p.membership_id));
      phrase = name ? `Assigned ${name}` : "Assigned a teammate";
      break;
    }
    case "node_unassigned": {
      const name = memberName(str(p.membership_id));
      phrase = name ? `Unassigned ${name}` : "Unassigned a teammate";
      break;
    }
    case "state_changed": {
      if (isTaskState(p.to)) {
        toState = p.to;
        phrase = `Moved to ${taskStateStyle(p.to).label}`;
      } else {
        phrase = "State changed";
      }
      break;
    }
    case "dependency_added": {
      const dep = nodeTitle(str(p.depends_on_node_id));
      phrase = dep ? `Linked: comes after ${quote(dep, "")}` : "Linked to another task";
      break;
    }
    case "dependency_removed": {
      const dep = nodeTitle(str(p.depends_on_node_id));
      phrase = dep ? `Unlinked from ${quote(dep, "")}` : "Removed a link";
      break;
    }
    case "note_added":
      phrase = "Note added";
      break;
    case "note_removed":
      phrase = "Note removed";
      break;
    case "target_date_set": {
      // Calm provenance, never a deadline tally (§A9). `to` is an ISO
      // `YYYY-MM-DD` string when set, null when cleared.
      const to = str(p.to);
      phrase = to ? `Target set to ${formatHistoryDate(to)}` : "Target cleared";
      break;
    }
    case "node_archived":
      phrase = "Archived";
      break;
    default:
      // Additive-only log: an unrecognised future type renders calmly.
      phrase = "Updated";
      break;
  }

  return {
    id: event.id,
    phrase,
    toState,
    accent: toState ? taskStateStyle(toState).fg : NEUTRAL_INK,
  };
}
